import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api/client";
import type { TxLog } from "../api/types";
import { useMe } from "../lib/me";
import { toast } from "../components/toast";

const REPORTING_CCYS = ["GBP", "USD", "EUR"];

// Per-transaction costs are tiny (a file can cost a fraction of a penny), so show
// up to 4 decimals — trailing zeros are dropped, so round amounts still read £0.16.
function money(n: number | null | undefined, ccy = "GBP"): string {
  if (n == null) return "—";
  try {
    return n.toLocaleString("en-GB", { style: "currency", currency: ccy, maximumFractionDigits: 4 });
  } catch {
    return `${ccy} ${n.toFixed(4)}`;
  }
}
function hours(seconds: number): string {
  return (seconds / 3600).toLocaleString("en-GB", { maximumFractionDigits: 1 }) + " h";
}
// A per-type cost cell: "·" when this row had no task of that type (backend omits
// it), money otherwise — so "no such task" reads differently from a £0.00 charge.
function costCell(v: number | undefined, ccy: string) {
  return v == null ? <span className="muted">·</span> : <>{money(v, ccy)}</>;
}

interface RecostResult {
  total: number;
  updated: number;
  by_source?: Record<string, number>;
  priced_from_plan?: number;
  still_zero?: number;
  reason?: string | null;
}
function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function downloadCsv(data: TxLog) {
  const head = ["time", "book", "owner", "type", "operation", "detail",
    "native_cost", "native_ccy", `cost_${data.reporting_currency}`];
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = data.events.map((e) => [
    e.at, e.project_name || e.project_id || "", e.owner_id || "",
    e.cost_type_label, e.operation, e.detail,
    e.native_cost ?? "", e.native_currency, e.cost ?? "",
  ].map(esc).join(","));
  const blob = new Blob([[head.join(","), ...rows].join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `transactions-${data.period.label}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function AdminTransactions() {
  const { data: me, isLoading: meLoading } = useMe();
  const [month, setMonth] = useState(currentMonth());
  const [ccy, setCcy] = useState("GBP");
  const [view, setView] = useState<"book" | "file" | "events">("book");
  const [recostNote, setRecostNote] = useState<RecostResult | null>(null);

  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["transactions", month, ccy],
    queryFn: () => api<TxLog>(`/api/admin/transactions?month=${month}&currency=${ccy}`),
    enabled: !!me?.is_superadmin,
  });

  const recost = useMutation({
    mutationFn: () =>
      api<RecostResult>(`/api/admin/transactions/recost?month=${month}`, { method: "POST" }),
    onSuccess: (res) => {
      setRecostNote(res);
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: ["pnl"] });
      if (res.total === 0) toast("No transcriptions to re-cost this month.", "info");
      else if (res.still_zero) toast(`${res.still_zero} transcription(s) still £0 — see the note above.`, "error");
      else toast(`Re-costed ${res.updated} of ${res.total} transcription(s).`, "success");
    },
    onError: (e: unknown) => toast(e instanceof ApiError ? e.message : "Re-cost failed.", "error"),
  });

  if (meLoading) return <div className="page muted">Loading…</div>;
  if (!me?.is_superadmin) return <Navigate to="/" replace />;

  const r = data?.reporting_currency || ccy;
  const types = data?.cost_types ?? [];
  const labels = data?.cost_type_labels ?? {};
  const unconverted = data?.fx?.unconverted ?? [];

  return (
    <div className="page admin-page">
      <div className="page-head">
        <div>
          <h1>Transactions</h1>
          <div className="sub">Every billable task this month, by book or as a log.<span className="muted"> · superadmin</span></div>
        </div>
      </div>

      <div className="card">
        <div className="cost-panel-head">
          <div className="tx-toggle">
            <button className={`btn sm${view === "book" ? " primary" : " ghost"}`} onClick={() => setView("book")}>By book</button>
            <button className={`btn sm${view === "file" ? " primary" : " ghost"}`} onClick={() => setView("file")}>By file</button>
            <button className={`btn sm${view === "events" ? " primary" : " ghost"}`} onClick={() => setView("events")}>Event log</button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn ghost sm" disabled={recost.isPending} onClick={() => recost.mutate()}
              title="Recompute this month's transcription costs from the current plan">
              {recost.isPending ? "Re-costing…" : "Re-cost"}
            </button>
            {view === "events" && data && data.events.length > 0 && (
              <button className="btn ghost sm" onClick={() => downloadCsv(data)}>Export CSV</button>
            )}
            <select className="input" value={ccy} onChange={(e) => setCcy(e.target.value)} style={{ width: 84 }}>
              {REPORTING_CCYS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input className="input" type="month" value={month}
              onChange={(e) => setMonth(e.target.value || currentMonth())} style={{ width: 150 }} />
          </div>
        </div>

        {isLoading && !data ? (
          <div className="muted">Loading…</div>
        ) : !data || data.totals.events === 0 ? (
          <div className="muted">No transactions recorded for {month}.</div>
        ) : (
          <>
            {unconverted.length > 0 && (
              <div className="cost-warn">
                {unconverted.length} amount(s) couldn't be converted to {r} (no rate for{" "}
                {[...new Set(unconverted.map((u) => u.currency))].join(", ")}) — excluded from the totals.
              </div>
            )}

            {recostNote && recostNote.total > 0 && (recostNote.still_zero ?? 0) > 0 && (
              <div className="cost-warn">
                Re-cost: {recostNote.priced_from_plan ?? 0} of {recostNote.total} transcription(s) priced from a plan;{" "}
                <strong>{recostNote.still_zero} still £0.</strong> {recostNote.reason}
              </div>
            )}

            {view === "book" ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Book</th>
                    {types.map((t) => <th key={t} className="r">{labels[t] || t}</th>)}
                    <th className="r">Total</th><th className="r">Audio</th><th className="r">Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_project.map((p) => (
                    <tr key={p.project_id || "none"}>
                      <td>
                        {p.project_id
                          ? <Link to={`/projects/${p.project_id}`}>{p.project_name || p.project_id.slice(0, 8)}</Link>
                          : <span className="muted">(no project)</span>}
                        {p.owner_id && <span className="muted"> · {p.owner_id.slice(0, 8)}</span>}
                      </td>
                      {types.map((t) => <td key={t} className="r">{costCell(p.costs[t], r)}</td>)}
                      <td className="r"><strong>{money(p.total, r)}</strong></td>
                      <td className="r muted">{p.audio_seconds > 0 ? hours(p.audio_seconds) : "—"}</td>
                      <td className="r muted">{p.n_events}{p.unpriced > 0 ? ` (${p.unpriced}!)` : ""}</td>
                    </tr>
                  ))}
                  <tr className="tx-total-row">
                    <td><strong>All books</strong></td>
                    {types.map((t) => <td key={t} className="r"><strong>{money(data.totals.by_type[t], r)}</strong></td>)}
                    <td className="r"><strong>{money(data.totals.grand_total, r)}</strong></td>
                    <td className="r"></td><td className="r muted">{data.totals.events}</td>
                  </tr>
                </tbody>
              </table>
            ) : view === "file" ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Audio file</th><th>Book</th>
                    {types.map((t) => <th key={t} className="r">{labels[t] || t}</th>)}
                    <th className="r">Total</th><th className="r">Audio</th><th className="r">Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_file.map((f, i) => (
                    <tr key={`${f.project_id || "none"}/${f.audio_filename || "book"}/${i}`}>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {f.audio_filename || <span className="muted">(book-level)</span>}
                      </td>
                      <td>
                        {f.project_id
                          ? <Link to={`/projects/${f.project_id}`}>{f.project_name || f.project_id.slice(0, 8)}</Link>
                          : <span className="muted">(no project)</span>}
                      </td>
                      {types.map((t) => <td key={t} className="r">{costCell(f.costs[t], r)}</td>)}
                      <td className="r"><strong>{money(f.total, r)}</strong></td>
                      <td className="r muted">{f.audio_seconds > 0 ? hours(f.audio_seconds) : "—"}</td>
                      <td className="r muted">{f.n_events}{f.unpriced > 0 ? ` (${f.unpriced}!)` : ""}</td>
                    </tr>
                  ))}
                  <tr className="tx-total-row">
                    <td colSpan={2}><strong>All files</strong></td>
                    {types.map((t) => <td key={t} className="r"><strong>{money(data.totals.by_type[t], r)}</strong></td>)}
                    <td className="r"><strong>{money(data.totals.grand_total, r)}</strong></td>
                    <td className="r"></td><td className="r muted">{data.totals.events}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>When</th><th>Book</th><th>Detail</th>
                    {types.map((t) => <th key={t} className="r">{labels[t] || t}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.events.map((e) => (
                    <tr key={e.id}>
                      <td className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        {new Date(e.at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td>{e.project_name || (e.project_id ? e.project_id.slice(0, 8) : <span className="muted">—</span>)}</td>
                      <td className="mono" style={{ fontSize: 12 }}>{e.detail}</td>
                      {types.map((t) => (
                        <td key={t} className="r">
                          {e.cost_type !== t
                            ? <span className="muted">·</span>
                            : e.converted
                              ? money(e.cost, r)
                              : <span className="muted" title={`${money(e.native_cost, e.native_currency)} — no rate`}>{money(e.native_cost, e.native_currency)}*</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
